import { useMemo } from "react";
import { cx } from "../../lib/cx";
import { Button } from "./Button";
import { alertTone, limitAlertTasks, type AlertCategory } from "./model";
import { dsCard, dsControl, toneBg } from "./styles";

export type WaitingTask = {
  id: string;
  category: AlertCategory;
  title: string;
  subtitle?: string;
  actionLabel: string;
  onAction?: () => void;
};

/** Opens another card. Same row as a task: mark, title, subtitle, pill. */
export type WaitingEntry = {
  id: string;
  title: string;
  subtitle?: string;
  actionLabel: string;
  tone: "coral" | "amber";
  onOpen: () => void;
};

type Props = {
  /** Section label, kept with the count under the greeting. */
  title: string;
  welcome?: string;
  date?: string;
  tasks: WaitingTask[];
  /** Most urgent rows to show. Extra tasks stay out of the card. */
  maxRows?: number;
  /** Full-row buttons after the tasks. They do not change the task count. */
  entries?: WaitingEntry[];
};

/** Same 20px mark as shop setup. Coral is a filled dot; amber is a ring. */
function Mark({ tone }: { tone: "coral" | "amber" }) {
  return (
    <span
      data-tone={tone}
      aria-hidden
      className={cx(
        "grid size-5 place-items-center rounded-full",
        tone === "coral" && toneBg(tone),
        tone === "amber" && "border-2 border-solid border-ds-amber bg-transparent",
      )}
    />
  );
}

/**
 * Waiting list as a shop-setup card. The greeting and date replace the page
 * title; the section label and count sit under them. One row per urgent task.
 */
export function WaitingCard({ title, welcome, date, tasks, maxRows = 3, entries = [] }: Props) {
  const { rows, total } = useMemo(() => limitAlertTasks(tasks, maxRows), [tasks, maxRows]);
  return (
    <section data-ds="" data-testid="ds-waiting" className={cx(dsCard, "flex h-full flex-col")} aria-label={title}>
      <header className="mb-ds-head">
        {welcome || date ? (
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            {welcome ? (
              <h1
                data-testid="ds-waiting-welcome"
                className="m-0 min-w-0 font-ds text-ds-page font-semibold text-ds-ink"
              >
                {welcome}
              </h1>
            ) : null}
            {date ? (
              <span data-testid="ds-waiting-date" className="text-ds-body font-normal text-ds-ink-3">
                {date}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className={cx("flex min-h-[34px] items-center gap-2.5", (welcome || date) && "mt-1.5")}>
          <h2 className="m-0 font-ds text-ds-card font-semibold text-ds-ink">{title}</h2>
          <span data-testid="ds-waiting-count" className="text-ds-card font-semibold text-ds-ink-3">
            {total}
          </span>
        </div>
      </header>
      <div>
        {rows.map((task) => (
          <div
            key={task.id}
            data-testid={`ds-waiting-${task.id}`}
            data-category={task.category}
            className="grid min-h-ds-row-setup grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-3"
          >
            <Mark tone={alertTone(task.category)} />
            <div className="min-w-0">
              <b className="block text-ds-item font-semibold text-ds-ink">{task.title}</b>
              {task.subtitle ? (
                <small className="mt-0.5 block text-ds-meta font-normal text-ds-ink-3">
                  {task.subtitle}
                </small>
              ) : null}
            </div>
            <Button variant="secondary" onClick={task.onAction}>
              {task.actionLabel}
            </Button>
          </div>
        ))}
        {entries.map((entry) => (
          <button
            key={entry.id}
            type="button"
            data-testid={`ds-waiting-entry-${entry.id}`}
            aria-haspopup="dialog"
            className={cx(
              dsControl,
              "group grid min-h-ds-row-setup w-full grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-3 bg-transparent px-0 py-0 text-left",
            )}
            onClick={entry.onOpen}
          >
            <Mark tone={entry.tone} />
            <span className="min-w-0">
              <b className="block text-ds-item font-semibold text-ds-ink">{entry.title}</b>
              {entry.subtitle ? (
                <small className="mt-0.5 block text-ds-meta font-normal text-ds-ink-3">{entry.subtitle}</small>
              ) : null}
            </span>
            <span
              aria-hidden
              className="inline-flex h-[38px] items-center justify-center rounded-ds-pill bg-ds-glass-strong px-[15px] text-ds-body font-semibold text-ds-ink group-hover:bg-ds-amber group-hover:text-ds-on-amber group-focus-visible:bg-ds-amber group-focus-visible:text-ds-on-amber"
            >
              {entry.actionLabel}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
